/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getRadiationSessions } from 'app/entities/radiation-session/radiation-session.reducer';
import { getEntities as getRadiationTypes } from 'app/entities/radiation-type/radiation-type.reducer';
import { getEntities as getRadiationBeamParameters } from 'app/entities/radiation-beam-parameter/radiation-beam-parameter.reducer';
import { getEntities as getRadiationDosimetries } from 'app/entities/radiation-dosimetry/radiation-dosimetry.reducer';
import { getEntities as getRadiationTechniques } from 'app/entities/radiation-technique/radiation-technique.reducer';
import { getEntities as getTreatmentCycles } from 'app/entities/treatment-cycle/treatment-cycle.reducer';
import { createEntity, getEntity, reset, updateEntity } from './treatment-radiation.reducer';

export const TreatmentRadiationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const radiationSessions = useAppSelector(state => state.radiationSession.entities);
  const radiationTypes = useAppSelector(state => state.radiationType.entities);
  const radiationBeamParameters = useAppSelector(state => state.radiationBeamParameter.entities);
  const radiationDosimetries = useAppSelector(state => state.radiationDosimetry.entities);
  const radiationTechniques = useAppSelector(state => state.radiationTechnique.entities);
  const treatmentCycles = useAppSelector(state => state.treatmentCycle.entities);
  const treatmentRadiationEntity = useAppSelector(state => state.treatmentRadiation.entity);
  const loading = useAppSelector(state => state.treatmentRadiation.loading);
  const updating = useAppSelector(state => state.treatmentRadiation.updating);
  const updateSuccess = useAppSelector(state => state.treatmentRadiation.updateSuccess);

  const handleClose = () => {
    navigate(`/treatment-radiation${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getRadiationSessions({}));
    dispatch(getRadiationTypes({}));
    dispatch(getRadiationBeamParameters({}));
    dispatch(getRadiationDosimetries({}));
    dispatch(getRadiationTechniques({}));
    dispatch(getTreatmentCycles({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...treatmentRadiationEntity,
      ...values,
      plannedRadiationType: radiationSessions.find(it => it.id.toString() === values.plannedRadiationType?.toString()),
      administeredRadiationType: radiationTypes.find(it => it.id.toString() === values.administeredRadiationType?.toString()),
      beamDetails: radiationBeamParameters.find(it => it.id.toString() === values.beamDetails?.toString()),
      doseDetails: radiationDosimetries.find(it => it.id.toString() === values.doseDetails?.toString()),
      techniquDetails: radiationTechniques.find(it => it.id.toString() === values.techniquDetails?.toString()),
      cycle: treatmentCycles.find(it => it.id.toString() === values.cycle?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...treatmentRadiationEntity,
          plannedRadiationType: treatmentRadiationEntity?.plannedRadiationType?.id,
          administeredRadiationType: treatmentRadiationEntity?.administeredRadiationType?.id,
          beamDetails: treatmentRadiationEntity?.beamDetails?.id,
          doseDetails: treatmentRadiationEntity?.doseDetails?.id,
          techniquDetails: treatmentRadiationEntity?.techniquDetails?.id,
          cycle: treatmentRadiationEntity?.cycle?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.treatmentRadiation.home.createOrEditLabel" data-cy="TreatmentRadiationCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.treatmentRadiation.home.createOrEditLabel">
              Create or edit a TreatmentRadiation
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="treatment-radiation-id"
                  label={translate('tinyMedicRestApp.treatmentRadiation.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.administered')}
                id="treatment-radiation-administered"
                name="administered"
                data-cy="administered"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.immobilized')}
                id="treatment-radiation-immobilized"
                name="immobilized"
                data-cy="immobilized"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.active')}
                id="treatment-radiation-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.cursor')}
                id="treatment-radiation-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.lastUpdated')}
                id="treatment-radiation-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.microSec')}
                id="treatment-radiation-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.treatmentRadiation.timestampEntry')}
                id="treatment-radiation-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="treatment-radiation-plannedRadiationType"
                name="plannedRadiationType"
                data-cy="plannedRadiationType"
                label={translate('tinyMedicRestApp.treatmentRadiation.plannedRadiationType')}
                type="select"
              >
                <option value="" key="0" />
                {radiationSessions
                  ? radiationSessions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="treatment-radiation-administeredRadiationType"
                name="administeredRadiationType"
                data-cy="administeredRadiationType"
                label={translate('tinyMedicRestApp.treatmentRadiation.administeredRadiationType')}
                type="select"
              >
                <option value="" key="0" />
                {radiationTypes
                  ? radiationTypes.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="treatment-radiation-beamDetails"
                name="beamDetails"
                data-cy="beamDetails"
                label={translate('tinyMedicRestApp.treatmentRadiation.beamDetails')}
                type="select"
              >
                <option value="" key="0" />
                {radiationBeamParameters
                  ? radiationBeamParameters.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="treatment-radiation-doseDetails"
                name="doseDetails"
                data-cy="doseDetails"
                label={translate('tinyMedicRestApp.treatmentRadiation.doseDetails')}
                type="select"
              >
                <option value="" key="0" />
                {radiationDosimetries
                  ? radiationDosimetries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="treatment-radiation-techniquDetails"
                name="techniquDetails"
                data-cy="techniquDetails"
                label={translate('tinyMedicRestApp.treatmentRadiation.techniquDetails')}
                type="select"
              >
                <option value="" key="0" />
                {radiationTechniques
                  ? radiationTechniques.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="treatment-radiation-cycle"
                name="cycle"
                data-cy="cycle"
                label={translate('tinyMedicRestApp.treatmentRadiation.cycle')}
                type="select"
              >
                <option value="" key="0" />
                {treatmentCycles
                  ? treatmentCycles.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/treatment-radiation" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TreatmentRadiationUpdate;
